import { EquipmentItem, EquipmentSlots, InventoryItem } from './equipmentTypes';

export const getSlotName = (slot: string | undefined | null): string => {
  if (slot === undefined || slot === null) return 'Unknown';
  return slot;
};

export const getSlotEnumValue = (slot: string): number => {
  const slotMap: { [key: string]: number } = {
    mainHand: 0, offHand: 1, head: 2, chest: 3,
    legs: 4, feet: 5, hands: 6, necklace: 7,
    ring1: 8, ring2: 9, belt: 10, back: 11,
  };
  return slotMap[slot];
};

export const canEquipInSlot = (item: InventoryItem, targetSlot: string): boolean => {
  const slot = item.equipmentSlot;
  if (slot !== undefined && slot !== null) {
    // Backend returns string enum values like "MainHand", "OffHand", etc.
    const slotMap: { [key: string]: string[] } = {
      mainHand: ['MainHand'], offHand: ['OffHand'], head: ['Head'], chest: ['Chest'],
      legs: ['Legs'], feet: ['Feet'], hands: ['Hands'], necklace: ['Necklace'],
      ring1: ['Ring1', 'Ring2'], ring2: ['Ring1', 'Ring2'], belt: ['Belt'], back: ['Back'],
    };
    const allowedSlots = slotMap[targetSlot];
    if (!allowedSlots) return false;
    return allowedSlots.includes(slot);
  }

  // Fall back to legacy itemType string: "Weapon", "Armor", "Accessory"
  const itemType = item.itemType;
  const legacySlotMap: { [key: string]: string[] } = {
    mainHand: ['Weapon'], offHand: ['Weapon'], head: ['Armor'], chest: ['Armor'],
    legs: ['Armor'], feet: ['Armor'], hands: ['Armor'], necklace: ['Accessory'],
    ring1: ['Accessory'], ring2: ['Accessory'], belt: ['Accessory'], back: ['Accessory'],
  };
  const allowedTypes = legacySlotMap[targetSlot];
  if (!allowedTypes) return false;
  return allowedTypes.includes(itemType);
};

// GH-93: compare an inventory item against what's currently equipped in its target slot

export interface StatDeltas {
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  speedBonus: number;
  minDamage: number;
  maxDamage: number;
}

export interface OffHandLoss {
  itemName: string;
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  speedBonus: number;
}

export interface ItemComparison {
  slotLabel: string;
  equippedItemName: string | null;
  deltas: StatDeltas;
  offHandLoss: OffHandLoss | null;
}

const RING_KEYS: (keyof EquipmentSlots)[] = ['ring1', 'ring2'];

// Backend returns PascalCase enum strings (e.g. "MainHand") on InventoryItem.equipmentSlot;
// EquipmentSlots keys are camelCase. Ring1/Ring2 both accept either ring item.
const BACKEND_SLOT_TO_KEYS: { [backendSlot: string]: (keyof EquipmentSlots)[] } = {
  MainHand: ['mainHand'], OffHand: ['offHand'], Head: ['head'], Chest: ['chest'],
  Legs: ['legs'], Feet: ['feet'], Hands: ['hands'], Necklace: ['necklace'],
  Ring1: RING_KEYS, Ring2: RING_KEYS, Belt: ['belt'], Back: ['back'],
};

const SLOT_LABELS: { [key in keyof EquipmentSlots]: string } = {
  mainHand: 'Main Hand', offHand: 'Off Hand', head: 'Head', chest: 'Chest',
  legs: 'Legs', feet: 'Feet', hands: 'Hands', necklace: 'Necklace',
  ring1: 'Ring 1', ring2: 'Ring 2', belt: 'Belt', back: 'Back',
};

export const getItemComparison = (item: InventoryItem, equipment: EquipmentSlots): ItemComparison | null => {
  const backendSlot = item.equipmentSlot;
  if (!backendSlot) return null;
  const candidateKeys = BACKEND_SLOT_TO_KEYS[backendSlot];
  if (!candidateKeys || candidateKeys.length === 0) return null;

  // Rings share one eligibility pool: diff against Ring 1 unless it's empty and Ring 2 is occupied
  const targetKey = candidateKeys.length === 1
    ? candidateKeys[0]
    : (!equipment[candidateKeys[0]] && equipment[candidateKeys[1]] ? candidateKeys[1] : candidateKeys[0]);

  const equipped: EquipmentItem | null = equipment[targetKey];

  const deltas: StatDeltas = {
    attackBonus: item.attackBonus - (equipped?.attackBonus || 0),
    defenseBonus: item.defenseBonus - (equipped?.defenseBonus || 0),
    hpBonus: item.hpBonus - (equipped?.hpBonus || 0),
    speedBonus: item.speedBonus - (equipped?.speedBonus || 0),
    minDamage: (item.minDamage || 0) - (equipped?.minDamage || 0),
    maxDamage: (item.maxDamage || 0) - (equipped?.maxDamage || 0),
  };

  // Equipping a 2H weapon in Main Hand also vacates Off Hand - surface that loss separately
  let offHandLoss: OffHandLoss | null = null;
  if (item.isTwoHanded && targetKey === 'mainHand' && equipment.offHand) {
    const oh = equipment.offHand;
    offHandLoss = {
      itemName: oh.name,
      attackBonus: oh.attackBonus,
      defenseBonus: oh.defenseBonus,
      hpBonus: oh.hpBonus,
      speedBonus: oh.speedBonus,
    };
  }

  return {
    slotLabel: SLOT_LABELS[targetKey],
    equippedItemName: equipped?.name || null,
    deltas,
    offHandLoss,
  };
};
